// ============================================================
// Login Page — Firebase Email/Password + Google Sign-In
// ============================================================

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { firebaseSignInWithEmail, firebaseSignInWithGoogle, auth } from '../lib/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const { setAuth } = useAuthStore();
    const navigate = useNavigate();

    /** Forgot Password — send Firebase reset email */
    const handleForgotPassword = async () => {
        if (!email) {
            toast.error('Please enter your email first');
            return;
        }
        try {
            await sendPasswordResetEmail(auth, email);
            setResetSent(true);
            toast.success('Password reset email sent! Check your inbox.');
        } catch (err: any) {
            const msg = err.code === 'auth/user-not-found' ? 'No account found with this email'
                : err.code === 'auth/too-many-requests' ? 'Too many attempts. Try again later.'
                    : err.message || 'Failed to send reset email';
            toast.error(msg);
        }
    };

    /** Exchange Firebase ID token for our app JWT */
    async function exchangeToken(idToken: string) {
        const res = await fetch(`${API_BASE}/auth/firebase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
        });

        const data = await res.json();

        if (!res.ok) {
            if (data.code === 'USER_NOT_FOUND') {
                // New Google user — redirect to signup
                toast.error('No account found. Please sign up first.');
                navigate('/signup');
                return;
            }
            throw new Error(data.error || 'Login failed');
        }

        setAuth({
            user: data.data.user,
            tenant: data.data.tenant,
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken,
            role: data.data.user.role,
        });

        toast.success('Welcome back!');
        navigate('/dashboard');
    }

    /** Email/Password login via Firebase */
    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const idToken = await firebaseSignInWithEmail(email, password);
            await exchangeToken(idToken);
        } catch (err: any) {
            // If Firebase auth fails, try legacy login (for users not yet in Firebase)
            if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
                try {
                    const res = await fetch(`${API_BASE}/auth/login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, password }),
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data.error || 'Login failed');

                    setAuth({
                        user: data.data.user,
                        tenant: data.data.tenant,
                        accessToken: data.data.accessToken,
                        refreshToken: data.data.refreshToken,
                        role: data.data.user.role,
                    });
                    toast.success('Welcome back!');
                    navigate('/dashboard');
                } catch (legacyErr: any) {
                    toast.error(legacyErr.message || 'Invalid credentials');
                }
            } else {
                const msg = err.code === 'auth/wrong-password' ? 'Incorrect password'
                    : err.code === 'auth/too-many-requests' ? 'Too many attempts. Try again later.'
                        : err.message || 'Login failed';
                toast.error(msg);
            }
        }
        setLoading(false);
    };

    /** Google Sign-In */
    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        try {
            const { idToken } = await firebaseSignInWithGoogle();
            await exchangeToken(idToken);
        } catch (err: any) {
            if (err.code !== 'auth/popup-closed-by-user') {
                toast.error(err.message || 'Google sign-in failed');
            }
        }
        setGoogleLoading(false);
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--color-bg-primary)',
        }}>
            <div className="card" style={{ width: 420, padding: 40 }}>
                <div style={{ textAlign: 'center', marginBottom: 32 }}>
                    <div style={{
                        width: 48, height: 48, borderRadius: 14, margin: '0 auto 16px',
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 800, fontSize: 22,
                    }}>H</div>
                    <h1 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: 4 }}>Welcome back</h1>
                    <p style={{ color: '#6b7280', fontSize: 14 }}>Sign in to your HireHub account</p>
                </div>

                {/* Google Sign-In Button */}
                <button
                    onClick={handleGoogleLogin}
                    disabled={googleLoading}
                    style={{
                        width: '100%', padding: '12px 16px', borderRadius: 10,
                        border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                        color: 'var(--color-text-primary)', fontWeight: 600, fontSize: 14,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                        marginBottom: 20, transition: 'border-color 0.2s',
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    {googleLoading ? 'Signing in...' : 'Continue with Google'}
                </button>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                    <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                    <span style={{ fontSize: 12, color: '#6b7280' }}>or</span>
                    <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                </div>

                {/* Email/Password Form */}
                <form onSubmit={handleEmailLogin}>
                    <div className="form-group">
                        <label className="form-label">Email</label>
                        <input className="form-input" type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Password</label>
                        <input className="form-input" type="password" value={password}
                            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                    </div>
                    <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
                        type="submit" disabled={loading}>
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                {/* Forgot Password */}
                <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <button onClick={handleForgotPassword} style={{
                        background: 'none', border: 'none', color: '#6366f1', fontSize: 13,
                        cursor: 'pointer', padding: 0, fontWeight: 500,
                    }}>
                        {resetSent ? '✓ Reset email sent!' : 'Forgot Password?'}
                    </button>
                </div>

                <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#6b7280' }}>
                    Don't have an account?{' '}
                    <Link to="/signup" style={{ color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>
                        Sign up
                    </Link>
                </p>
            </div>
        </div>
    );
}
